import { formatDate } from '@/utils/format';

/**
 * What choosing a plan would do, worded once for every billing screen.
 *
 * The backend decides (`plan.change` on the plans it offers a customer):
 * `UPGRADE` starts now and is billed now; `DOWNGRADE` waits for the end of the
 * period already bought; `SWITCH` is a plan priced in another currency, which
 * is never compared on price and also waits for the end of the paid period. When a plan comes
 * without `change` (the admin catalogue) the same rule is applied here from
 * what the customer pays now.
 */

export const CHANGE = Object.freeze({
  CURRENT: 'CURRENT',
  UPGRADE: 'UPGRADE',
  DOWNGRADE: 'DOWNGRADE',
  SWITCH: 'SWITCH',
});

function monthly(priceCents, interval) {
  return interval === 'YEARLY' ? Math.floor(priceCents / 12) : priceCents;
}

/**
 * `current` describes what the customer pays now: `{ code, price_cents,
 * currency, interval, running }`. `running` false (or no current plan) means
 * nothing paid is running, so every paid plan is an upgrade.
 */
export function planChangeKind(plan, current) {
  if (plan?.change) return plan.change;
  if (!plan) return null;
  if (current && plan.code === current.code) return CHANGE.CURRENT;
  const paying =
    current && current.running !== false ? monthly(current.price_cents ?? 0, current.interval) : 0;
  const target = monthly(plan.price_cents ?? 0, plan.interval);
  if (target === 0) return paying > 0 ? CHANGE.DOWNGRADE : CHANGE.UPGRADE;
  if (paying === 0) return CHANGE.UPGRADE;
  if (
    current?.currency &&
    plan.currency &&
    current.currency.toUpperCase() !== plan.currency.toUpperCase()
  ) {
    return CHANGE.SWITCH;
  }
  return target >= paying ? CHANGE.UPGRADE : CHANGE.DOWNGRADE;
}

/** Whether a change of this kind starts now (and is billed now). */
export function startsNow(kind) {
  return kind === CHANGE.UPGRADE;
}

/**
 * The button text for a plan, as the plan pages have always worded it:
 * "Current plan", "Upgrade" from a free plan, "Switch to monthly" / "Switch to
 * yearly" from a paid one, and "Switch to Free". When the change happens is
 * explained in the confirm dialog (`planChangeCopy`), never on the button.
 */
export function planButtonLabel(
  plan,
  { isCurrent = false, onPaidPlan = false, freeName = 'Free' } = {},
) {
  if (isCurrent) return 'Current plan';
  if (!plan || (plan.price_cents ?? 0) === 0) return `Switch to ${freeName}`;
  if (!onPaidPlan) return 'Upgrade';
  return `Switch to ${plan.interval === 'YEARLY' ? 'yearly' : 'monthly'}`;
}

/** When a change of this kind takes effect, in words: "Starts now" or
 * "Starts on 8 Nov 2026, when your current period ends". */
export function planChangeTiming(kind, periodEnd) {
  if (startsNow(kind)) return 'Starts now';
  return periodEnd
    ? `Starts on ${formatDate(periodEnd)}, when your current period ends`
    : 'Starts when your current period ends';
}

/** The confirm dialog's title, explanation and button for a change. */
export function planChangeCopy(kind, { planName, currentName, periodEnd }) {
  const timing = planChangeTiming(kind, periodEnd);
  if (!startsNow(kind)) {
    return {
      title: `Switch to ${planName}?`,
      timing,
      description: `${timing}. You keep ${currentName ?? 'your current plan'} until then, nothing is billed today, and you can keep your current plan any time before.`,
      confirm: 'Confirm switch',
    };
  }
  return {
    title: `Switch to ${planName}?`,
    timing,
    description:
      kind === CHANGE.SWITCH
        ? `${timing}. ${planName} is priced in a different currency, so a new period begins today with an invoice in its own currency.`
        : `${timing}. A new period begins today with an invoice for it.`,
    confirm: 'Confirm switch',
  };
}
